import MenuButton from "molecules/menu-button/menu-button";
import { ChecklistStatus } from "models/enumerations/notebooks/checklist-item-status";
import React, { useEffect, useState } from "react";
import { useLocalization } from "utilities/hooks/use-localization";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface ChecklistStatusBadgeProps {
    disabled?: boolean;
    onClick?: (value: ChecklistStatus) => void;
    status?: ChecklistStatus;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "c-checklist-status-badge";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ChecklistStatusBadge: React.FC<ChecklistStatusBadgeProps> = (
    props: ChecklistStatusBadgeProps
) => {
    const { t } = useLocalization();
    const { disabled = false } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<
        ChecklistStatus | undefined
    >(props.status);

    useEffect(() => {
        setCurrentStatus(props.status);
    }, [props.status]);

    const handleSelect = (value: ChecklistStatus) => {
        setCurrentStatus(value);
        setShowStatusSelectionMenu(false);
        props.onClick?.(value);
    };

    const [showStatusSelectionMenu, setShowStatusSelectionMenu] =
        React.useState(false);

    const getCssModifier = (status: ChecklistStatus | undefined) => {
        switch (status) {
            case ChecklistStatus.NotStarted:
                return " -not-started";
            case ChecklistStatus.Pending:
                return " -pending";
            case ChecklistStatus.Pass:
                return " -pass";
            case ChecklistStatus.Fail:
                return " -fail";
            default:
                return " -default-status";
        }
    };

    const getStatusLabel = (status: ChecklistStatus | undefined) => {
        switch (status) {
            case ChecklistStatus.NotStarted:
                return t("checklist-item-status-not-started");
            case ChecklistStatus.Pending:
                return t("checklist-item-status-pending");
            case ChecklistStatus.Pass:
                return t("checklist-item-status-pass");
            case ChecklistStatus.Fail:
                return t("checklist-item-status-fail");
            default:
                return t("checklist-item-status-null");
        }
    };

    const showStatusMenu = () => {
        setShowStatusSelectionMenu(true);
    };

    const statusButton = (
        <button
            onClick={() => showStatusMenu()}
            className={`${CSS_CLASS_NAME}__menu-item ${getCssModifier(
                currentStatus as ChecklistStatus
            )}`}>
            <span
                className={`${CSS_CLASS_NAME}__menu-badge ${getCssModifier(
                    currentStatus as ChecklistStatus
                )}`}>
                {getStatusLabel(currentStatus)}
            </span>
        </button>
    );

    return (
        <MenuButton
            cssClassName={CSS_CLASS_NAME}
            customButtonContent={statusButton}
            disabled={disabled}
            externalIsOpen={isOpen}
            externalSetIsOpen={setIsOpen}
            displayMobileView={false}>
            {!disabled &&
                showStatusSelectionMenu &&
                Object.values(ChecklistStatus)
                    .filter((key) => !isNaN(Number(key)))
                    .map((itemStatus) => (
                        <button
                            className={`${CSS_CLASS_NAME}__menu-item `}
                            key={itemStatus}
                            onClick={(e) => {
                                handleSelect(itemStatus as ChecklistStatus);
                                setIsOpen(false);
                            }}>
                            <span
                                className={`${CSS_CLASS_NAME}__menu-badge ${getCssModifier(
                                    itemStatus as ChecklistStatus
                                )}  `}>
                                {getStatusLabel(itemStatus as ChecklistStatus)}
                            </span>
                        </button>
                    ))}
        </MenuButton>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default ChecklistStatusBadge;

// #endregion Exports
